import { RestClient } from '@libs/rest-client';
import type { GridSchema } from '@ps-smart-cp/ppfw-client';
import type { MstImport, MstImportRaw, MstListSearch, OutputOperations, OutputOrder } from '../models';
import {
  MstListSearchRaw,
  MstRegistration,
  MstRegistrationRaw,
  OutputOperationsRaw,
  OutputOrderRaw,
} from '../models';
import { createMstImportFormData } from '../services';

interface JobNoRaw {
  jobNo: string;
}

type SearchKey = { code: string } | { id: number };

/** マスタ登録の関数 */
type RegistrationFunction<M extends MstRegistration> = (m: M) => Promise<void>;

/** マスタ更新の関数 */
type UpdateFunction<M extends MstRegistration> = (id: number, m: M) => Promise<void>;

/** マスタ一件取得の関数 */
type FetchFunction<M extends MstRegistration> = (searchKey: SearchKey) => Promise<M>;

/** マスタ削除の関数 */
type DeleteFunction = (id: number, version: number) => Promise<void>;

/** マスタグリッド検索の関数 */
type ListRequestFunction<SM extends MstListSearch, RM extends MstRegistration> = (
  page: number,
  rowsPerPage: number,
  mstSearch: SM,
  outputOrder: OutputOrder,
) => Promise<GridSchema<RM>>;

/** マスタ出力の関数 */
type ExportFunction<SM extends MstListSearch> = (
  mstSearch: SM,
  outputOrder: OutputOrder,
  outputOps: OutputOperations,
) => Promise<JobNoRaw>;

/** マスタ取込みの関数 */
type ImportFunction<T extends MstImport> = (file: File | null, mstImport: T) => Promise<JobNoRaw>;

/** マスタModel class */
interface SchemaClass<M, R> {
  fromRaw: (raw: R) => M;
}

/** マスタRaw class */
interface RecordClass<M, R> {
  fromModel: (model: M) => R;
}

interface MstCRUDFunctionOptions {
  /**
   * backendを指定する
   * @default 'fa'
   */
  system?: 'auth' | 'fa';
}

function getRestClient(options?: MstCRUDFunctionOptions) {
  return RestClient[options?.system ?? 'fa'];
}

/**
 * マスタ登録用の関数を作る関数
 *
 * @param path APIのパス
 * @param rawClass see {@link recordClass}
 */
function hoMstRegistration<
  RM extends MstRegistration = MstRegistration,
  RR extends MstRegistrationRaw = MstRegistrationRaw,
>(
  path: string,
  rawClass?: RecordClass<RM, RR>,
  options?: MstCRUDFunctionOptions,
): (extraParams?: object) => RegistrationFunction<RM> {
  return (params = {}) =>
    async (model: RM): Promise<void> =>
      getRestClient(options).post(path, {
        ...params,
        ...(rawClass?.fromModel ?? MstRegistrationRaw.baseFromModel)(model),
      });
}

function hoMstUpdate<
  RM extends MstRegistration = MstRegistration,
  RR extends MstRegistrationRaw = MstRegistrationRaw,
>(
  path: string,
  rawClass?: RecordClass<RM, RR>,
  options?: MstCRUDFunctionOptions,
): (extraParams?: object) => UpdateFunction<RM> {
  return (params = {}) =>
    async (id: number, model: RM) =>
      getRestClient(options).put(`${path}?${new URLSearchParams({ id: id.toString() }).toString()}`, {
        ...params,
        ...(rawClass?.fromModel ?? MstRegistrationRaw.baseFromModel)(model),
      });
}

function hoMstFetch<
  RM extends MstRegistration = MstRegistration,
  RR extends MstRegistrationRaw = MstRegistrationRaw,
>(
  path: string,
  schemaClass?: SchemaClass<RM, RR>,
  options?: MstCRUDFunctionOptions,
): (extraParams?: object) => FetchFunction<RM> {
  return (params = {}) =>
    async (searchKey: SearchKey): Promise<RM> => {
      const raw =
        'id' in searchKey
          ? await getRestClient(options).get<RR>(path, { id: searchKey.id, ...params })
          : await getRestClient(options).get<RR>(`${path}/by-code`, {
              code: searchKey.code,
              ...params,
            });

      return (schemaClass?.fromRaw ?? MstRegistration.baseFromRaw)(raw) as RM;
    };
}

function hoMstDelete(
  path: string,
  options?: MstCRUDFunctionOptions,
): (extraParams?: Record<string, string>) => DeleteFunction {
  return (extraParams = {}) =>
    async (id: number, version: number): Promise<void> =>
      getRestClient(options).delete(path, {
        ...extraParams,
        id: id.toString(),
        version: version.toString(),
      });
}

function hoMstListRequest<
  SM extends MstListSearch = MstListSearch,
  SR extends MstListSearchRaw = MstListSearchRaw,
  RM extends MstRegistration = MstRegistration,
  RR extends MstRegistrationRaw = MstRegistrationRaw,
>(
  path: string,
  searchRecordClass?: RecordClass<SM, SR>,
  registrationSchemaClass?: SchemaClass<RM, RR>,
  options?: MstCRUDFunctionOptions,
): (extraParams?: object) => ListRequestFunction<SM, RM> {
  return (extraParams = {}) =>
    async (page: number, rowsPerPage: number, mstListSearch: SM, outputOrder: OutputOrder) => {
      const { rows, totalNumber } = await getRestClient(options).post<GridSchema<RR>>(
        `${path}/list-request`,
        {
          mstListSearch: {
            ...extraParams,
            ...(searchRecordClass?.fromModel ?? MstListSearchRaw.baseFromModel)(mstListSearch),
          },
          outputOrder: OutputOrderRaw.fromModel(outputOrder),
          pageInfo: { page, rowsPerPage },
        },
      );

      return {
        rows: rows.map(registrationSchemaClass?.fromRaw ?? MstRegistration.baseFromRaw) as RM[],
        totalNumber,
      };
    };
}

function hoMstExport<M extends MstListSearch = MstListSearch, R extends MstListSearchRaw = MstListSearchRaw>(
  path: string,
  rawClass?: RecordClass<M, R>,
  options?: MstCRUDFunctionOptions,
): (extraParams?: object) => ExportFunction<M> {
  return (extraParams = {}) =>
    async (mstListSearch: M, outputOrder: OutputOrder, outputOperations: OutputOperations) =>
      getRestClient(options).post<JobNoRaw>(`${path}/output`, {
        mstListSearch: {
          ...extraParams,
          ...(rawClass?.fromModel ?? MstListSearchRaw.baseFromModel)(mstListSearch),
        },
        outputOrder: OutputOrderRaw.fromModel(outputOrder),
        outputOperations: OutputOperationsRaw.fromModel(outputOperations),
      });
}

function hoMstImport<M extends MstImport, R extends MstImportRaw>(
  path: string,
  recordClass?: { fromModel: (file: File, mstImport: M) => R },
  options?: MstCRUDFunctionOptions,
): (extraParams?: object) => ImportFunction<M> {
  return (extraParams = {}) =>
    async (file: File | null, mstImport: M) => {
      const formData = createMstImportFormData(extraParams, file, mstImport, recordClass);
      return getRestClient(options).formPost<JobNoRaw>(`${path}-import`, formData);
    };
}

export {
  hoMstDelete,
  hoMstExport,
  hoMstFetch,
  hoMstImport,
  hoMstListRequest,
  hoMstRegistration,
  hoMstUpdate,
};
export type {
  DeleteFunction,
  ExportFunction,
  FetchFunction,
  ImportFunction,
  ListRequestFunction,
  RegistrationFunction,
  SearchKey,
  UpdateFunction,
};
